import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { hrApi } from '@/api';
import { EmployeeSelect } from '@/components/selects';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { DatePicker } from '@/components/ui/DatePicker';
import type { Employee360Response, HrLifecycleEvent } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function HrEmployee360Page() {
  const { employeeId } = useParams<{ employeeId: string }>();
  const [selectedId, setSelectedId] = useState(employeeId ?? '');
  const [view, setView] = useState<Employee360Response | null>(null);
  const [lifecycle, setLifecycle] = useState<HrLifecycleEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [eventType, setEventType] = useState('CONFIRMED');
  const [effectiveDate, setEffectiveDate] = useState('');

  useEffect(() => {
    if (employeeId) {
      setSelectedId(employeeId);
    }
  }, [employeeId]);

  useEffect(() => {
    if (!selectedId) return;
    setLoading(true);
    Promise.all([hrApi.employee360(Number(selectedId)), hrApi.lifecycleHistory(Number(selectedId))])
      .then(([v, h]) => {
        setView(v);
        setLifecycle(h);
      })
      .catch((err) => showApiError(err, 'Failed to load Employee 360'))
      .finally(() => setLoading(false));
  }, [selectedId]);

  const recordLifecycle = async () => {
    if (!selectedId || !effectiveDate) return;
    try {
      await hrApi.recordLifecycleChange({ employeeId: Number(selectedId), eventType, effectiveDate });
      notify.success('Lifecycle event recorded');
      const h = await hrApi.lifecycleHistory(Number(selectedId));
      setLifecycle(h);
    } catch (err) {
      showApiError(err, 'Lifecycle update failed');
    }
  };

  return (
    <div>
      <PageHeader title="Employee 360" description="Unified profile across HR domains" breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'HR', to: '/hr' }, { label: 'Employee 360' }]} />
      <div className="mb-4 flex flex-wrap gap-2">
        <EmployeeSelect className="max-w-md" value={selectedId} onChange={setSelectedId} />
      </div>
      {loading && <p className="text-sm text-slate-500">Loading…</p>}
      {view && (
        <div className="grid gap-4 lg:grid-cols-2">
          <section className="card p-4">
            <h3 className="font-semibold">Profile</h3>
            <p className="text-sm">{view.employee.firstName} {view.employee.lastName ?? ''} ({view.employee.employeeCode})</p>
            {view.employment && (
              <ul className="mt-2 text-sm text-slate-600">
                <li>Department: {view.employment.departmentName ?? '—'}</li>
                <li>Designation: {view.employment.designationName ?? '—'}</li>
                <li>Manager: {view.employment.managerName ?? '—'}</li>
              </ul>
            )}
          </section>
          <section className="card p-4">
            <h3 className="font-semibold">Lifecycle history</h3>
            <ul className="mb-3 text-sm">
              {lifecycle.map((e) => (
                <li key={e.id}>{e.effectiveDate}: {e.eventType} ({e.previousStage} → {e.newStage})</li>
              ))}
            </ul>
            <Input label="Event type" value={eventType} onChange={(e) => setEventType(e.target.value)} />
            <DatePicker label="Effective date" value={effectiveDate} onChange={setEffectiveDate} />
            <Button className="mt-2" onClick={() => void recordLifecycle()}>Record event</Button>
          </section>
          <section className="card p-4">
            <h3 className="font-semibold">Documents</h3>
            <ul className="text-sm">{(view.documents ?? []).map((d) => <li key={d.id}>{d.documentName} {d.expiryDate ? `(exp ${d.expiryDate})` : ''}</li>)}</ul>
          </section>
          <section className="card p-4">
            <h3 className="font-semibold">Training</h3>
            <ul className="text-sm">{(view.training ?? []).map((t) => <li key={t.courseId}>{t.title}: {t.completionStatus}</li>)}</ul>
          </section>
          <section className="card p-4">
            <h3 className="font-semibold">Assets</h3>
            <ul className="text-sm">{(view.assets ?? []).map((a) => <li key={a.id}>{a.assetType} {a.assetTag} ({a.status})</li>)}</ul>
          </section>
          <section className="card p-4">
            <h3 className="font-semibold">Performance</h3>
            <ul className="text-sm">{(view.performance ?? []).map((p) => <li key={p.reviewId}>{p.cycleName}: {p.score ?? '—'} ({p.status})</li>)}</ul>
          </section>
          <section className="card p-4">
            <h3 className="font-semibold">Recent attendance</h3>
            <ul className="text-sm">{(view.recentAttendance ?? []).slice(0, 5).map((a) => <li key={a.id}>{a.date} — {a.status}</li>)}</ul>
          </section>
          <section className="card p-4">
            <h3 className="font-semibold">Leave & payroll</h3>
            <ul className="text-sm">
              {(view.recentLeaves ?? []).map((l) => <li key={l.id}>{l.leaveType}: {l.status}</li>)}
              {(view.recentPayroll ?? []).map((p) => <li key={p.runId}>{p.runCode}: {p.netAmount}</li>)}
            </ul>
          </section>
        </div>
      )}
    </div>
  );
}
